/**
 * FO-426 P3 (batch-58) - WELK werkschema voedt de "verwachte diensten" in de
 * klant-jaarplanning?
 *
 * Die vraag werd op twee plekken beantwoord met twee sorteringen: de
 * jaarplanning-route las `[{isDefault:'desc'},{createdAt:'desc'}]`, de
 * werkschema-lijst voor de backoffice `[{isDefault:'desc'},{createdAt:'asc'}]`.
 * Bij een klant met een dagdienst-schema als standaard plus twee rotatie-
 * schema's wees de badge in het beheer daardoor een ANDER schema aan dan het
 * portaal telde - en de opschoon-banner noemde de verkeerde als "actief".
 *
 * Deze functie is die ene regel. Puur: geen DB, geen I/O, geen sortering ter
 * plekke opnieuw uitvinden.
 */
import { heeftDekkingRotatie } from './dienst-slots'

/** De velden die bepalen of en hoe zwaar een werkschema meetelt. */
export interface WerkschemaVolgordeVelden {
  isDefault: boolean
  /** Aanmaakmoment; `string` omdat een DTO hem als ISO-tekst doorgeeft. */
  createdAt: Date | string
  /** Prisma `Json?`; alleen een niet-lege array is een bruikbaar patroon. */
  rotationPattern: unknown
  rotationCycleDays: number | null
  rotationStartDate: Date | string | null
}

function alsTijd(waarde: Date | string): number {
  return waarde instanceof Date ? waarde.getTime() : Date.parse(waarde)
}

/**
 * De werkschema's die meetellen voor de klant-jaarplanning, in ACTIEF-volgorde:
 * standaard eerst, daarna het meest recente. Het EERSTE element is het schema
 * dat de verwachte diensten levert; staan er meer, dan is dat opschoonwerk aan
 * de beheerkant.
 *
 * "Meetellend" = draagt een bruikbare rotatie (`heeftDekkingRotatie`, dezelfde
 * poort als de dekking-engine). Een dagdienst-schema zonder rotatie levert geen
 * verwachte diensten en telt hier dus niet mee - ook niet als het de standaard is.
 */
export function meetellendeWerkschemas<T extends WerkschemaVolgordeVelden>(
  schemas: readonly T[],
): T[] {
  return schemas
    .filter((s) =>
      heeftDekkingRotatie({
        rotationPattern: s.rotationPattern,
        rotationCycleDays: s.rotationCycleDays,
        rotationStartDate:
          s.rotationStartDate == null
            ? null
            : s.rotationStartDate instanceof Date
              ? s.rotationStartDate
              : new Date(s.rotationStartDate),
      }),
    )
    .slice()
    .sort((a, b) => {
      if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1
      return alsTijd(b.createdAt) - alsTijd(a.createdAt)
    })
}

/**
 * Het schema dat de klant-jaarplanning telt, of `null` als de klant geen enkel
 * rotatie-schema heeft (dan toont het portaal "geen vast rooster").
 */
export function actiefJaarplanningSchema<T extends WerkschemaVolgordeVelden>(
  schemas: readonly T[],
): T | null {
  return meetellendeWerkschemas(schemas)[0] ?? null
}
